import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import type { ValidationError } from "yup";

import {
  getDefaultUnderwritingQuestionsForm,
  mapFormKeyToUnderwritingQuestion,
  mergeUnderwritingAnswers,
  toUnderwritingQuestionsObject,
  underwritingQuestionsFormValidation,
  type UnderwritingQuestionsForm,
} from "@covertree/new-business-forms";
import {
  useUwModal,
  type UwContext,
} from "@covertree/new-business-uw-questions";

import {
  selectEndorsementUnderwritingAnswers,
  selectEndorsementUnderwritingQuestions,
  selectIsViewOnly,
  useEndorsementSelector,
} from "@redux/slices/endorsement";
import { useEndorsementOutletContext } from "@layouts/EndorsementLayout/EndorsementLayout";

const FORM_KEYS = Object.keys(
  mapFormKeyToUnderwritingQuestion
) as (keyof UnderwritingQuestionsForm)[];

export const useUnderwritingSection = () => {
  const { policy } = useEndorsementOutletContext();
  const { getValues, reset, register, unregister } =
    useFormContext<UnderwritingQuestionsForm>();
  const modal = useUwModal();
  const [isSeeded, setIsSeeded] = useState(false);

  const viewOnly = useEndorsementSelector(selectIsViewOnly);
  const questions = useEndorsementSelector(
    selectEndorsementUnderwritingQuestions
  );
  const answers = useEndorsementSelector(selectEndorsementUnderwritingAnswers);

  const questionsObject = useMemo(
    () => toUnderwritingQuestionsObject(questions ?? []),
    [questions]
  );

  const defaults = useMemo(
    () =>
      answers &&
      getDefaultUnderwritingQuestionsForm(
        mergeUnderwritingAnswers(
          answers.policyLevel,
          answers.unitLevel ? [answers.unitLevel] : []
        )
      ),
    [answers]
  );

  // Fills missing answers; `reset` keeps them out of dirtyFields.
  // The cards read the form on render, so they mount only once it is seeded.
  useEffect(() => {
    if (!defaults) return;

    const values = getValues();
    if (!Object.keys(defaults).every((key) => key in values)) {
      reset({ ...defaults, ...values }, { keepDirtyValues: true });
    }

    setIsSeeded(true);
  }, [defaults, getValues, reset]);

  // The Coverage step has no form resolver (it would skip the coverages' own
  // rules), so the NBS schema is applied per field while this section is mounted.
  useEffect(() => {
    if (viewOnly) return;

    const schema = underwritingQuestionsFormValidation(questionsObject);

    FORM_KEYS.forEach((key) =>
      register(key, {
        validate: (_, values) =>
          schema.validateAt(key, values).then(
            () => true,
            (error: ValidationError) => error.message
          ),
      })
    );

    return () =>
      FORM_KEYS.forEach((key) =>
        unregister(key, {
          keepValue: true,
          keepDefaultValue: true,
          keepDirty: true,
        })
      );
  }, [viewOnly, questionsObject, register, unregister]);

  // The cards read answers via `getValues`, which does not re-render on change.
  useWatch({ name: FORM_KEYS });

  const uwContext: UwContext = {
    state: policy.units?.[0]?.address?.state ?? "",
    questions: questionsObject,
    isFormSubmitting: false,
    setModalCondition: modal.setModalCondition,
  };

  return {
    isReady: isSeeded,
    viewOnly,
    uwContext,
    modalCondition: modal.modalConditionData,
    hasAnswerChanged: modal.hasAnswerChanged,
  };
};
